/**
 * The global cycle facet (electinfo/sites#194, #355).
 *
 * One election cycle for the money on every us.elect.info page, or all cycles
 * (the default, exactly what the site shows without a selection). The cycle is
 * the URL's ?cycle=YYYY, so a scoped page can be shared and rendered on the
 * server: us-pages filters its money lists by it, the client cards
 * (money flow, graph, leaf lists, vendor clients and purposes) read
 * currentCycle(). While a cycle is selected, links to other us.elect.info pages
 * carry it.
 *
 * The facet is a member control in the shared identity bar: shown to every
 * visitor, usable once signed in. Choosing a cycle reloads the page with the
 * new parameter, since the server-rendered lists depend on it.
 */

import { belowHeaderInsertPoint } from './page-header';

/** FEC cycles start with 1980 in the graph's sources: the first one the menu offers. */
export const FIRST_CYCLE = 1980;

/**
 * The cycles ?cycle= may name: even years 1976-2100, the rule api-graphql
 * (validCycle), us-pages (pages.elect.info/src/cycle.ts) and the nginx cache-key
 * map share, so the facet and a server-rendered page always agree on the scope.
 */
export const VALID_CYCLES = { min: 1976, max: 2100 } as const;

export function latestCycle(now: Date = new Date()): number {
  const y = now.getFullYear();
  return y % 2 === 0 ? y : y + 1;
}

/** An even cycle year in VALID_CYCLES, else null (all cycles). */
export function parseCycle(value: string | null | undefined): number | null {
  if (!value || !/^\d{4}$/.test(value)) return null;
  const c = Number(value);
  return c % 2 === 0 && c >= VALID_CYCLES.min && c <= VALID_CYCLES.max ? c : null;
}

/** The selected cycle of a URL (default: this page). */
export function currentCycle(url: string = window.location.href): number | null {
  return parseCycle(new URL(url, window.location.href).searchParams.get('cycle'));
}

/** `url` with ?cycle= set to `cycle`, or removed for all cycles. */
export function withCycle(url: string, cycle: number | null): string {
  const u = new URL(url, window.location.href);
  if (cycle === null) u.searchParams.delete('cycle');
  else u.searchParams.set('cycle', String(cycle));
  return u.pathname + u.search + u.hash;
}

/**
 * The scope notice under the header while a cycle is selected, shared with
 * us-pages' server-rendered pages (same element, so a page carries one).
 */
export function cycleNoticeHtml(cycle: number | null): string {
  if (!cycle) return '';
  return `<p class="ei-cycle-notice" data-cycle="${cycle}">Money lists show the ${cycle - 1}\u2013${cycle} cycle. ` +
    'Totals and profile figures cover all cycles.</p>';
}

/** Exact year-token membership for existing generated GraphQL list queries. */
export function cycleCsvWhere(cycle: number): { OR: { cycles: Record<string, string> }[] } {
  const year = String(cycle);
  return { OR: [
    { cycles: { eq: year } }, { cycles: { startsWith: year + ',' } },
    { cycles: { endsWith: ',' + year } }, { cycles: { contains: ',' + year + ',' } },
  ] };
}

/** Scope disclosure for specialized indexes which do not extend EntityIndex. */
export function showCycleIndexNotice(supported: boolean): void {
  const cycle = currentCycle();
  if (cycle === null || document.querySelector('.ei-cycle-notice')) return;
  const notice = document.createElement('p');
  notice.className = 'ei-cycle-notice';
  notice.dataset.cycle = String(cycle);
  notice.textContent = supported
    ? `Results and counts show entities active in the ${cycle - 1}–${cycle} cycle. Profile figures cover all cycles.`
    : 'Results and counts cover all cycles; election-cycle membership is not available for this entity type.';
  const point = belowHeaderInsertPoint();
  point?.parentNode?.insertBefore(notice, point);
}

export function cycleOptions(now: Date = new Date()): number[] {
  const out: number[] = [];
  for (let c = latestCycle(now); c >= FIRST_CYCLE; c -= 2) out.push(c);
  return out;
}

/** Links that should keep the selected cycle: same-origin pages, not the API, files or anchors. */
export function carriesCycle(link: HTMLAnchorElement): boolean {
  if (!link.href || link.target === '_blank' || link.hasAttribute('download')) return false;
  const u = new URL(link.href, window.location.href);
  if (u.origin !== window.location.origin || u.searchParams.has('cycle')) return false;
  if (/^\/(api|auth|identity|signin|outpost\.goauthentik\.io|js|css|data)\//.test(u.pathname)) return false;
  return !/\.[a-z0-9]{2,5}$/i.test(u.pathname) || u.pathname.endsWith('.html');
}

const CALENDAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M5 1h1.5v1.5h3V1H11v1.5h2A1.5 1.5 0 0 1 14.5 4v9A1.5 1.5 0 0 1 13 14.5H3A1.5 1.5 0 0 1 1.5 13V4A1.5 1.5 0 0 1 3 2.5h2zm-2 5v7h10V6zm1.5 1.5h2v2h-2zm3.5 0h2v2H8z"/></svg>';

const STYLE = `
.ei-cycle-menu{position:absolute;z-index:1000;min-width:150px;max-height:min(60vh,360px);overflow:auto;margin:0;padding:4px;
  list-style:none;background:#fff;border:1px solid #e2e8f0;border-radius:6px;box-shadow:0 8px 24px rgba(26,54,93,.16);font-size:.85rem}
.ei-cycle-menu button{display:flex;justify-content:space-between;gap:12px;width:100%;padding:6px 10px;border:0;border-radius:4px;
  background:none;color:#2d3748;font:inherit;text-align:left;cursor:pointer}
.ei-cycle-menu button:hover,.ei-cycle-menu button:focus-visible{background:#edf2f7;outline:none}
.ei-cycle-menu button[aria-checked="true"]{color:#1a365d;font-weight:700}
.ei-cycle-menu button[aria-checked="true"]::after{content:"\\2713"}
.ei-cycle-menu .ei-cycle-menu__sep{height:1px;margin:4px 6px;background:#e2e8f0}
`;

function ensureStyle(doc: Document): void {
  if (doc.getElementById('ei-cycle-css')) return;
  const style = doc.createElement('style');
  style.id = 'ei-cycle-css';
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

/** The cycle menu under the control; picking a cycle reloads with it. */
function openMenu(button: HTMLElement, selected: number | null, go: (cycle: number | null) => void): void {
  const doc = button.ownerDocument;
  doc.getElementById('ei-cycle-menu')?.remove();
  ensureStyle(doc);
  const menu = doc.createElement('div');
  menu.id = 'ei-cycle-menu';
  menu.className = 'ei-cycle-menu';
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Election cycle facet');
  const item = (label: string, cycle: number | null): HTMLButtonElement => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.setAttribute('role', 'menuitemradio');
    b.setAttribute('aria-checked', String(cycle === selected));
    b.dataset.cycle = cycle === null ? 'all' : String(cycle);
    b.textContent = label;
    b.addEventListener('click', () => {
      close();
      if (cycle !== selected) go(cycle);
    });
    return b;
  };
  menu.append(item('All cycles', null));
  const sep = doc.createElement('div');
  sep.className = 'ei-cycle-menu__sep';
  // A valid cycle outside the offered range (typed into the URL) is listed too, checked.
  const offered = cycleOptions();
  if (selected !== null && !offered.includes(selected)) offered.unshift(selected);
  menu.append(sep, ...offered.map((c) => item(`${c - 1}–${c}`, c)));

  const r = button.getBoundingClientRect();
  menu.style.top = `${r.bottom + window.scrollY + 4}px`;
  doc.body.appendChild(menu);
  const left = Math.min(r.left + window.scrollX, window.scrollX + doc.documentElement.clientWidth - menu.offsetWidth - 8);
  menu.style.left = `${Math.max(8, left)}px`;
  button.setAttribute('aria-expanded', 'true');

  const onDoc = (e: Event): void => {
    if (!menu.contains(e.target as Node) && !button.contains(e.target as Node)) close();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
      button.focus();
    }
  };
  function close(): void {
    menu.remove();
    button.setAttribute('aria-expanded', 'false');
    doc.removeEventListener('mousedown', onDoc, true);
    doc.removeEventListener('keydown', onKey, true);
  }
  doc.addEventListener('mousedown', onDoc, true);
  doc.addEventListener('keydown', onKey, true);
  (menu.querySelector<HTMLElement>('[aria-checked="true"]') ?? menu.querySelector<HTMLElement>('button'))?.focus();
}

type ControlHost = { electinfoBarControls?: { push: (...c: object[]) => void } | object[] };

/** Register the facet in the identity bar and keep the cycle on internal links. */
export function initCycleFacet(): void {
  const w = window as unknown as ControlHost & { __electinfoCycleFacet?: boolean; electinfoCycle?: unknown };
  if (w.__electinfoCycleFacet) return;
  w.__electinfoCycleFacet = true;
  const selected = currentCycle();
  if (/^\/(blocks|counties|places|postalcodes|precincts|tracts)\/(?:index\.html)?$/.test(window.location.pathname)) {
    showCycleIndexNotice(false);
  }
  w.electinfoCycle = { get: currentCycle, options: cycleOptions };

  const label = selected ? `Cycle ${selected}` : 'All cycles';
  const queue = (w.electinfoBarControls = w.electinfoBarControls || []) as { push: (...c: object[]) => void };
  queue.push({
    id: 'cycle',
    label,
    name: 'the cycle facet',
    // An entitlement (ei-cycle-viewers, staff, premium): others are offered Request access.
    requires: 'cycles',
    title: selected
      ? `Money on these pages is for the ${selected - 1}–${selected} cycle. Change the cycle`
      : 'Show money for one election cycle on every page',
    icon: CALENDAR,
    onActivate: (ctx: { button: HTMLElement }) =>
      openMenu(ctx.button, selected, (cycle) => window.location.assign(withCycle(window.location.href, cycle))),
    update: (button: HTMLElement) => {
      button.setAttribute('aria-haspopup', 'menu');
      button.setAttribute('aria-expanded', 'false');
      if (selected) button.setAttribute('aria-pressed', 'true');
    },
  });

  if (selected) {
    // Carry the cycle on internal links (capture: before page scripts' own handlers).
    document.addEventListener(
      'click',
      (e) => {
        const link = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
        if (link && carriesCycle(link)) link.href = withCycle(link.href, selected);
      },
      true,
    );
  }
}
